
import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";

interface App {
  id: string;
  bundle_id: string;
  app_name: string;
  app_icon_url?: string;
  version_code?: string;
  category?: string;
  tags: string[];
  description?: string;
  created_at: string;
  updated_at: string;
}

interface AppCatalogActionsProps {
  selectedAppIds: string[];
  onSelectionChange: (appIds: string[]) => void;
  onRefresh: () => void;
}

export const useAppCatalogActions = ({ selectedAppIds, onSelectionChange, onRefresh }: AppCatalogActionsProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const deleteApp = useMutation(api.apps.delete.app);

  const handleBulkDelete = async () => {
    if (selectedAppIds.length === 0) return;

    setIsDeleting(true);
    try {
      // Delete apps one by one using Convex mutations
      for (const appId of selectedAppIds) {
        await deleteApp({ app: appId as any });
      }

      toast({
        title: "Success",
        description: `${selectedAppIds.length} app(s) deleted successfully`,
      });

      onSelectionChange([]);
      onRefresh();
    } catch (error) {
      console.error('Error deleting apps:', error);
      toast({
        title: "Error",
        description: "Failed to delete selected apps",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteApp = async (appId: string) => {
    setIsDeleting(true);
    try {
      await deleteApp({ app: appId as any });

      toast({
        title: "Success",
        description: "App deleted successfully",
      });

      onRefresh();
    } catch (error) {
      console.error('Error deleting app:', error);
      toast({
        title: "Error",
        description: "Failed to delete app",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    isDeleting,
    handleBulkDelete,
    handleDeleteApp,
  };
};
